import moment from 'moment'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { FormEvent, useEffect, useRef, useState } from 'react'
import { Button } from 'components/Button'
import { Input, Card } from 'components/ui'
import { api } from 'lib/api'
import { debug, logError } from 'lib/logging'
import { errorToast } from 'lib/toast'

/**
 * Forgot password page component
 * @description Handles password reset functionality, including sending reset emails and processing reset codes
 * @returns {JSX.Element} The forgot password page with form for email/password reset
 */
export default function Forgot() {
  const params = useSearchParams()
  const [submitted, setSubmitted] = useState(false)
  const [expired, setExpired] = useState(false)
  let formRef = useRef<HTMLFormElement>(null)
  const [code] = useState(params.get(`code`))
  const [time] = useState(params.get(`time`))

  useEffect(() => {
    setExpired(moment(time).diff(moment(), `minutes`) > 10)
  }, [time])

  /**
   * Handles form submission for password reset
   * @description Processes password reset request or completes password reset with code
   * @param {FormEvent} event - The form submission event
   * @returns {Promise<void>} Promise that resolves when the request is complete
   */
  const submit = async (event: FormEvent) => {
    event.preventDefault()

    let formData = new FormData(formRef.current!)

    const email = formData.get(`email`)
    const password = formData.get(`password`)

    try {
      await api.post(`/api/auth/reset`, { email, password, code })
      if (code) {
        document.location = `/`
      } else {
        setSubmitted(true)
      }
    } catch (err) {
      logError(`error resetting password`, err)
      errorToast(`There was a problem resetting your password. Please try again later.`)
    }
  }

  debug(`auth`, `code`, code, `expired`, expired)

  return (
    <div id='forgot-page' className='center-all bg-slate-900 text-white'>
      {submitted || expired ? (
        <div className='text-lg text-cyan-400'>
          {submitted ? `An email has been sent to you with a password reset link.` : `This link has expired`}
        </div>
      ) : (
        <Card mode='primary' className='w-[400px]'>
          <form ref={formRef} onSubmit={submit}>
            {code && !expired && (
              <div className='font-weight-bold mb-4'>
                Enter your email address and new password below to reset your password.
              </div>
            )}
            <div className='mb-3'>
              <label htmlFor='email' className='block text-sm font-medium text-slate-100 mb-1'>
                Email Address
              </label>
              <Input
                name='email'
                id='email'
                type='email'
                mode='primary'
                fullWidth={true}
                glow={true}
                required
                defaultValue={params.get(`email`) || ``}
              />
            </div>
            {code && !expired && (
              <div className='mb-3'>
                <label htmlFor='password' className='block text-sm font-medium text-slate-100 mb-1'>
                  New Password
                </label>
                <Input
                  type='password'
                  name='password'
                  id='password'
                  mode='secondary'
                  fullWidth={true}
                  glow={true}
                  required
                  defaultValue={process.env.DEFAULT_PASSWORD}
                />
              </div>
            )}
            <div className='mb-5 mt-8'>
              <Button type='submit'>Reset Password</Button>
            </div>
            <div className=''>
              <Link href='/login' className='btn btn-link block w-full text-center text-white'>
                Return to login
              </Link>
            </div>
          </form>
        </Card>
      )}
    </div>
  )
}
